import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import './App.css';
import { isAuthenticated, handleCallback, login, logout, getUserInfo, getUserGroups } from './auth';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const SKILL_INIT_PROMPT_PLACEHOLDER = `You are an HR skill taxonomy expert specializing in HR Tech companies.

## Company Context
HCG is South Korea's #1 Full-Stack HR Tech company (25 years history) providing:
- HR Tech Solutions: e-HR packages (hunel for enterprise, JaDE for SMB, talenx SaaS)
- AI Powered HR Services: AI Performance Management, HR Dashboard, HR Assistant, Payroll Agent, Talent Match
- Solution Operation & Maintenance: HR system support, patch, education
- Payroll Outsourcing: payroll calculation, social insurance, year-end tax settlement
- HR Consulting: performance/reward, organization design, culture, digital HR & analytics

Key roles: HR Tech developers, AI/data scientists, HR consultants, SI consultants, payroll specialists, customer success, security/infra, product managers.

## Task
For each skill provided (Korean name, English name, Korean description), generate:
1. **hard_soft**: classify as "Hard" or "Soft"
   - Hard: technical, domain-specific, measurable skills (tools, methods, knowledge)
   - Soft: behavioral, interpersonal, cognitive abilities
2. **description**: English skill description following the structure below

## Description Guidelines
- Format: "The ability to [core action] [target] using [method/tool], achieving [purpose/outcome]."
- **Base your description on the Korean description provided** — preserve the key concepts, context, and nuances
- Write in natural English, general enough to apply across roles
- Include representative tools/methods in parentheses where relevant
- Length: 1 concise sentence (100–250 characters)

## Output Format
Respond in valid JSON object:
{
  "skills": [
    {
      "skill_name": "exact English skill name as provided",
      "hard_soft": "Hard" or "Soft",
      "description": "The ability to ..."
    },
    ...
  ]
}`;

const ALL_TABS = [
  { id: 'skill_init', label: 'Skill Init' },
  { id: 'extraction', label: 'Skill Analysis' },
  { id: 'match', label: 'Candidate Match' },
  { id: 'talent', label: 'Talent Match' },
  { id: 'search', label: 'Employee Search' },
  { id: 'team_composer', label: 'Team Composer' },
];

// 그룹별 접근 가능한 탭 (없는 그룹은 DEFAULT_ALLOWED_TABS 적용)
const GROUP_TAB_PERMISSIONS = {
  skill_hr_admin: ALL_TABS.map(t => t.id),   // 모든 탭
};
const DEFAULT_ALLOWED_TABS = ['skill_init', 'extraction', 'match'];  // 그룹 없음 → Init + 분석/매칭

const computeAllowedTabs = (groups) => {
  if (!groups || groups.length === 0) return DEFAULT_ALLOWED_TABS;
  const set = new Set();
  groups.forEach(g => (GROUP_TAB_PERMISSIONS[g] || []).forEach(t => set.add(t)));
  return set.size === 0 ? DEFAULT_ALLOWED_TABS : Array.from(set);
};

function App() {
  // 권한 계산 (초기 1회)
  const initialGroups = getUserGroups();
  const initialAllowed = computeAllowedTabs(initialGroups);
  const initialMain = initialAllowed.slice(0, 4);   // 허용된 탭 중 앞 4개를 메인으로
  const isAdmin = initialGroups.includes('skill_hr_admin');

  const [allowedTabs, setAllowedTabs] = useState(initialAllowed);
  const [activeTab, setActiveTab] = useState(initialAllowed[0] || 'skill_init');
  const [activeClient, setActiveClient] = useState('com_p');
  const [clientList, setClientList] = useState([]);
  const [mainTabIds, setMainTabIds] = useState(initialMain);
  const [tabOrder, setTabOrder] = useState(initialAllowed);
  const [isTabSettingsOpen, setIsTabSettingsOpen] = useState(false);
  const [draggedTabId, setDraggedTabId] = useState(null);

  // Skill Init 상태
  const [skillInitFile, setSkillInitFile] = useState(null);
  const [skillInitPrompt, setSkillInitPrompt] = useState('');
  const [skillInitResults, setSkillInitResults] = useState([]);
  const [skillInitSnapshot, setSkillInitSnapshot] = useState([]);   // 유사 그룹 매핑용 스냅샷
  const [skillInitSimPairs, setSkillInitSimPairs] = useState([]);
  const [skillInitGroups, setSkillInitGroups] = useState([]);
  const [skillInitCosine, setSkillInitCosine] = useState('0.85');
  const [skillInitClustering, setSkillInitClustering] = useState('0.90');
  const [skillInitSearch, setSkillInitSearch] = useState('');
  const [skillInitHighlightIdx, setSkillInitHighlightIdx] = useState(null);
  const [cosineOpen, setCosineOpen] = useState(true);
  const [clusterOpen, setClusterOpen] = useState(true);
  const [noticeOpen, setNoticeOpen] = useState(false);

  // Team Composer 상태
  const [teamProject, setTeamProject] = useState('');
  const [teamRoles, setTeamRoles] = useState([]);
  const [teamExpansion, setTeamExpansion] = useState(null);
  const [expandedRoleIdx, setExpandedRoleIdx] = useState(null);
  const [expandedEmpKey, setExpandedEmpKey] = useState(null);
  const [isSkillInitSaving, setIsSkillInitSaving] = useState(false);

  // Skill Analysis 상태
  const [rows, setRows] = useState([{ type: 'T', text: '', value: '' }]);
  const [topSkills, setTopSkills] = useState([]);
  const [errorValue, setErrorValue] = useState(null);
  
  // 실제 모델 응답을 위한 상태
  const [needsRecommend, setNeedsRecommend] = useState(false);
  const [taskResiduals, setTaskResiduals] = useState([]);
  
  // [추가] 알림 배지 전용 상태 (버튼 노출 여부와 분리)
  const [showBadge, setShowBadge] = useState(false);

  // Talent Match 상태
  const [talentRows, setTalentRows] = useState([{ text: '' }]);
  const [talentProjectSkills, setTalentProjectSkills] = useState([]);
  const [talentCandidates, setTalentCandidates] = useState([]);

  // Employee Search 상태
  const [searchName, setSearchName] = useState('');
  const [searchEmployeeSkills, setSearchEmployeeSkills] = useState([]);
  const [searchEmployeeInfo, setSearchEmployeeInfo] = useState(null);
  const [searchSimilarEmployees, setSearchSimilarEmployees] = useState([]);

  // Resume Match 상태
  const [jdRows, setJdRows] = useState([{ text: '' }]);
  const [resumeRows, setResumeRows] = useState([{ text: '' }]);
  const [matchResults, setMatchResults] = useState([]);
  const [expandedResultIndex, setExpandedResultIndex] = useState(null);

  const [showAnalysis, setShowAnalysis] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // New Skills 상태 및 전용 로딩 프로그레스
  const [isNewSkillsLoading, setIsNewSkillsLoading] = useState(false);
  const [newSkillsResult, setNewSkillsResult] = useState([]); 
  const [newSkillsProgress, setNewSkillsProgress] = useState(0);
  const newSkillsProgressRef = useRef(null);

  const [progress, setProgress] = useState(0);
  const [userInfo, setUserInfo] = useState(null);
  const progressRef = useRef(null);
  const [isGuideOpen, setIsGuideOpen] = useState(false);

  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [tooltip, setTooltip] = useState({ visible: false, text: '', x: 0, y: 0 });
  const [editorConfig, setEditorConfig] = useState({ isOpen: false, type: '', index: null, text: '' });
  const [alertConfig, setAlertConfig] = useState({ isOpen: false, message: '', type: 'error' });

  useEffect(() => {
    const authenticated = handleCallback();
    if (!authenticated && !isAuthenticated()) {
      login();
    } else {
      setUserInfo(getUserInfo());
      // 토큰 갱신 시 권한 다시 계산 (URL hash로 토큰이 들어온 경우)
      const newAllowed = computeAllowedTabs(getUserGroups());
      setAllowedTabs(newAllowed);
      setTabOrder(newAllowed);
      setMainTabIds(newAllowed.slice(0, 4));
      if (!newAllowed.includes(activeTab)) {
        setActiveTab(newAllowed[0] || 'skill_init');
      }

      // 클라이언트 목록을 백엔드에서 가져오기
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const isAdmin = getUserGroups().includes('skill_hr_admin');
      axios.get(`${apiUrl}/api/clients`)
        .then(res => {
          const HIDDEN_CLIENTS = ['com_p', 'com_s'];
          const all = res.data?.clients || [];
          // admin은 전체 노출, 그 외 default 유저는 com_p/com_s 숨김
          const list = isAdmin ? all : all.filter(c => !HIDDEN_CLIENTS.includes(c));
          setClientList(list);
          if (list.length > 0 && !list.includes(activeClient)) {
            setActiveClient(list[0]);
          }
        })
        .catch(err => console.warn('클라이언트 목록 로드 실패:', err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showAlert = (message, type = 'error') => {
    setAlertConfig({ isOpen: true, message, type });
  };

  // 탭 전환 처리 (결과는 초기화하되 알림 점만 제거, 버튼 조건은 유지)
  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
    setShowAnalysis(false);
    setShowBadge(false); // 탭 이동 시 빨간 점 제거
    // 주의: needsRecommend는 초기화하지 않음으로써 가이드 내 Update 항목 노출 유지 여부 결정
  };

  // 가이드 토글 (알림 점만 제거)
  const toggleGuide = () => {
    if (!isGuideOpen) {
      setShowBadge(false); // 가이드 오픈 시 빨간 점 제거
    }
    setIsGuideOpen(!isGuideOpen);
  };

  // --- Talent Match 조작 ---
  const addTalentRow = () => setTalentRows([...talentRows, { text: '' }]);
  const deleteTalentRow = (index) => {
    if (talentRows.length > 1) setTalentRows(talentRows.filter((_, i) => i !== index));
    else showAlert("최소 한 개의 행은 있어야 합니다.");
  };
  const handleTalentInputChange = (index, val) => {
    const updated = [...talentRows];
    updated[index].text = val;
    setTalentRows(updated);
  };

  // --- Skill Analysis 조작 ---
  const addRow = () => setRows([...rows, { type: 'T', text: '', value: '' }]);
  const deleteRow = (index) => {
    if (rows.length > 1) setRows(rows.filter((_, i) => i !== index));
    else showAlert("최소 한 개의 행은 있어야 합니다.");
  };
  const handleInputChange = (index, field, val) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = val;
    setRows(updatedRows);
  };

  // --- Resume Match 조작 ---
  const addMatchRow = (target) => {
    if (target === 'jd') setJdRows([...jdRows, { text: '' }]);
    else setResumeRows([...resumeRows, { text: '' }]);
  };
  const deleteMatchRow = (target, index) => {
    const list = target === 'jd' ? jdRows : resumeRows;
    if (list.length > 1) {
      if (target === 'jd') setJdRows(jdRows.filter((_, i) => i !== index));
      else setResumeRows(resumeRows.filter((_, i) => i !== index));
    } else {
      showAlert("최소 한 개의 행은 있어야 합니다.");
    }
  };
  const handleMatchInputChange = (target, index, val) => {
    const updated = target === 'jd' ? [...jdRows] : [...resumeRows];
    updated[index].text = val;
    target === 'jd' ? setJdRows(updated) : setResumeRows(updated);

    if (editorConfig.isOpen && editorConfig.type === target && editorConfig.index === index) {
      setEditorConfig(prev => ({ ...prev, text: val }));
    }
  };

  // --- Skill Init: 유사 Skill 클릭 시 해당 행(UID 기반)으로 스크롤 + 강조 ---
  const scrollToSkillRowByUid = (uid) => {
    const currentIdx = skillInitResults.findIndex(r => r._uid === uid);
    if (currentIdx === -1) {
      showAlert("해당 Skill은 Result에서 제거되었습니다.");
      return;
    }
    if (skillInitSearch) setSkillInitSearch('');
    setSkillInitHighlightIdx(currentIdx);
    setTimeout(() => {
      const row = document.querySelector(`[data-row-idx="${currentIdx}"]`);
      if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  // 사용자 입력(클릭/키보드/휠) 발생 시 강조 해제
  useEffect(() => {
    if (skillInitHighlightIdx === null) return;
    const clear = () => setSkillInitHighlightIdx(null);
    // 클릭으로 강조가 시작된 직후 같은 클릭이 해제하지 않도록 약간 지연
    const timer = setTimeout(() => {
      document.addEventListener('pointerdown', clear);
      document.addEventListener('keydown', clear);
      document.addEventListener('wheel', clear);
    }, 250);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('pointerdown', clear);
      document.removeEventListener('keydown', clear);
      document.removeEventListener('wheel', clear);
    };
  }, [skillInitHighlightIdx]);

  // --- Skill Init 조작 ---
  const runSkillInit = async () => {
    if (!skillInitFile) { showAlert("Excel 파일을 업로드해주세요."); return; }

    if (skillInitResults.length > 0) {
      if (!window.confirm("현재 결과와 편집 내용이 모두 사라지고 새로 분석합니다. 계속할까요?")) return;
    }

    const cosineVal = parseFloat(skillInitCosine);
    const clusteringVal = parseFloat(skillInitClustering);
    if (isNaN(cosineVal) || isNaN(clusteringVal)) {
      showAlert("Cosine과 Clustering 임계값을 올바르게 입력해주세요."); return;
    }

    setIsLoading(true);
    setProgress(0);
    // 진행바 시뮬레이션 (실제 모듈 진행률은 알 수 없으니 점진적 증가)
    progressRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev < 60) return prev + 0.45;
        if (prev < 90) return prev + 0.18;
        if (prev < 99) return prev + 0.05;
        return 99;
      });
    }, 150);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const formData = new FormData();
      formData.append('file', skillInitFile);
      formData.append('prompt', skillInitPrompt || '');
      formData.append('sim_threshold', String(cosineVal));
      formData.append('component_threshold', String(clusteringVal));

      const response = await axios.post(
        `${apiUrl}/api/${activeClient}/skill-init/run`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data && response.data.status === 'error') throw new Error(response.data.message);

      const recs = (response.data.records || []).map((r, i) => ({ ...r, _uid: `r_${Date.now()}_${i}` }));
      setSkillInitResults(recs);
      setSkillInitSnapshot(recs);   // 유사 그룹의 인덱스가 가리킬 스냅샷
      setSkillInitSimPairs(response.data.sim_pairs || []);
      setSkillInitGroups(response.data.groups || []);

    } catch (e) {
      const msg = e.response?.data?.message || e.message;
      showAlert(`처리 중 오류: ${msg}`);
    } finally {
      if (progressRef.current) clearInterval(progressRef.current);
      setProgress(100);
      setTimeout(() => setIsLoading(false), 200);
    }
  };

  const rerunSkillInit = async () => {
    if (skillInitResults.length === 0) { showAlert("재실행할 데이터가 없습니다."); return; }
    const cosineVal = parseFloat(skillInitCosine);
    const clusteringVal = parseFloat(skillInitClustering);
    if (isNaN(cosineVal) || isNaN(clusteringVal)) {
      showAlert("Cosine과 Clustering 값을 올바르게 입력해주세요."); return;
    }

    setIsLoading(true);
    setProgress(0);
    progressRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev < 60) return prev + 0.7;
        if (prev < 90) return prev + 0.3;
        if (prev < 99) return prev + 0.08;
        return 99;
      });
    }, 150);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const payload = {
        records: skillInitResults.filter(r => !r._deleted),
        sim_threshold: cosineVal,
        component_threshold: clusteringVal,
      };

      const response = await axios.post(
        `${apiUrl}/api/${activeClient}/skill-init/rerun`,
        payload
      );

      if (response.data && response.data.status === 'error') throw new Error(response.data.message);

      const recs = (response.data.records || []).map((r, i) => ({ ...r, _uid: `r_${Date.now()}_${i}` }));
      setSkillInitResults(recs);
      setSkillInitSnapshot(recs);   // 유사 그룹의 인덱스가 가리킬 스냅샷
      setSkillInitSimPairs(response.data.sim_pairs || []);
      setSkillInitGroups(response.data.groups || []);

    } catch (e) {
      const msg = e.response?.data?.message || e.message;
      showAlert(`재실행 오류: ${msg}`);
    } finally {
      if (progressRef.current) clearInterval(progressRef.current);
      setProgress(100);
      setTimeout(() => setIsLoading(false), 200);
    }
  };

  const handleSkillInitCellChange = (index, field, value) => {
    const updated = [...skillInitResults];
    updated[index][field] = value;
    setSkillInitResults(updated);
  };

  // 삭제 = 즉시 제거가 아니라 '삭제 예정' 토글 (soft delete)
  const deleteSkillInitRow = (index) => {
    const aliveCount = skillInitResults.filter(r => !r._deleted).length;
    const target = skillInitResults[index];
    if (!target._deleted && aliveCount <= 1) {
      showAlert("최소 한 개의 행은 남아 있어야 합니다.");
      return;
    }
    const next = [...skillInitResults];
    next[index] = { ...next[index], _deleted: !next[index]._deleted };
    setSkillInitResults(next);
  };

  // 유사 Skill 섹션에서의 복구 (uid 기준 _deleted 해제)
  const restoreSkillInitRow = (uid) => {
    setSkillInitResults(skillInitResults.map(r =>
      r._uid === uid ? { ...r, _deleted: false } : r
    ));
  };

  // --- Team Composer 실행 ---
  const runTeamComposer = async () => {
    if (!teamProject.trim()) { showAlert("Project 내용을 입력해주세요."); return; }

    setIsLoading(true);
    setShowAnalysis(false);
    setExpandedEmpKey(null);
    setProgress(0);
    progressRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev < 60) return prev + 0.45;
        if (prev < 90) return prev + 0.18;
        if (prev < 99) return prev + 0.05;
        return 99;
      });
    }, 150);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await axios.post(
        `${apiUrl}/api/${activeClient}/team-composer`,
        { project: teamProject }
      );

      if (response.data && response.data.status === 'error') throw new Error(response.data.message);

      setTeamRoles(response.data.roles || []);
      setTeamExpansion(response.data.sbase_expansion || null);
      setExpandedRoleIdx(0);
      setShowAnalysis(true);
    } catch (e) {
      const msg = e.response?.data?.message || e.message;
      showAlert(`Team Composer 오류: ${msg}`);
    } finally {
      if (progressRef.current) clearInterval(progressRef.current);
      setProgress(100);
      setTimeout(() => setIsLoading(false), 200);
    }
  };

  const saveSkillInitToDB = async () => {
    const finalRecords = skillInitResults.filter(r => !r._deleted);
    if (finalRecords.length === 0) { showAlert("저장할 결과가 없습니다."); return; }
    setIsSkillInitSaving(true);
    try {
      // 1. Excel 다운로드 (삭제 예정 행 / Embedding 제외)
      const wb = XLSX.utils.book_new();
      const header = ['skill_name', 'skill_name_ko', 'hard_soft', 'description'];
      const rows = finalRecords.map(r => [
        r.skill_name, r.skill_name_ko, r.hard_soft, r.description,
      ]);
      const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
      ws['!cols'] = [{ wch: 25 }, { wch: 25 }, { wch: 10 }, { wch: 60 }];
      XLSX.utils.book_append_sheet(wb, ws, 'Skill Init Results');
      XLSX.writeFile(wb, 'skill_init_results.xlsx');

      // 2. DB 반영 (삭제 예정 행 제외)
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await axios.post(
        `${apiUrl}/api/${activeClient}/skill-init/save`,
        { records: finalRecords }
      );
      if (response.data && response.data.status === 'error') throw new Error(response.data.message);

      showAlert("DB에 반영되었습니다. Skill Analysis / Candidate Match를 바로 사용할 수 있습니다.", 'success');

    } catch (e) {
      const msg = e.response?.data?.message || e.message;
      showAlert(`DB 반영 실패: ${msg}`);
    } finally {
      setIsSkillInitSaving(false);
    }
  };

  const openEditor = (type, index) => {
    if (editorConfig.isOpen && editorConfig.type === type && editorConfig.index === index) {
      setEditorConfig({ isOpen: false, type: '', index: null, text: '' });
    } else {
      let text = '';
      if (type === 'jd') text = jdRows[index].text;
      else if (type === 'resume') text = resumeRows[index].text;
      else if (type === 'prompt') text = skillInitPrompt;
      else if (type === 'skill_desc') text = skillInitResults[index]?.description || '';
      else if (type === 'team_project') text = teamProject;
      setEditorConfig({ isOpen: true, type, index, text });
    }
  };

  const handleEditorChange = (e) => {
    const newText = e.target.value;
    setEditorConfig(prev => ({ ...prev, text: newText }));
    if (editorConfig.type === 'prompt') {
      setSkillInitPrompt(newText);
    } else if (editorConfig.type === 'skill_desc') {
      handleSkillInitCellChange(editorConfig.index, 'description', newText);
    } else if (editorConfig.type === 'team_project') {
      setTeamProject(newText);
    } else {
      handleMatchInputChange(editorConfig.type, editorConfig.index, newText);
    }
  };

  // --- 엑셀 양식 다운로드 ---
  const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    if (activeTab === 'extraction') {
      const ws = XLSX.utils.aoa_to_sheet([['TYPE', 'Description', 'Weight']]);
      ws['!cols'] = [{ wch: 8 }, { wch: 50 }, { wch: 10 }];
      XLSX.utils.book_append_sheet(wb, ws, 'Skill Analysis');
      XLSX.writeFile(wb, 'skill_analysis_template.xlsx');
    } else if (activeTab === 'talent') {
      const ws = XLSX.utils.aoa_to_sheet([['Task Description']]);
      ws['!cols'] = [{ wch: 60 }];
      XLSX.utils.book_append_sheet(wb, ws, 'Talent Match');
      XLSX.writeFile(wb, 'talent_match_template.xlsx');
    } else if (activeTab === 'skill_init') {
      const ws = XLSX.utils.aoa_to_sheet([['스킬명(한)', '스킬명(영)', '설명']]);
      ws['!cols'] = [{ wch: 25 }, { wch: 25 }, { wch: 60 }];
      XLSX.utils.book_append_sheet(wb, ws, 'Skill Init');
      XLSX.writeFile(wb, 'skill_init_template.xlsx');
    } else {
      const ws = XLSX.utils.aoa_to_sheet([['JD', 'Candidate']]);
      ws['!cols'] = [{ wch: 50 }, { wch: 50 }];
      XLSX.utils.book_append_sheet(wb, ws, 'Candidate Match');
      XLSX.writeFile(wb, 'candidate_match_template.xlsx');
    }
  };

  // --- 엑셀 업로드 ---
  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const wb = XLSX.read(evt.target.result, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
      const dataRows = data.slice(1).filter(row => row.some(cell => cell !== undefined && cell !== ''));

      if (activeTab === 'extraction') {
        const newRows = dataRows.map(row => ({
          type: (String(row[0] || 'T')).toUpperCase().startsWith('R') ? 'R' : 'T',
          text: String(row[1] || ''),
          value: row[2] !== undefined ? String(row[2]) : '',
        }));
        if (newRows.length > 0) setRows(newRows);
      } else if (activeTab === 'talent') {
        const newRows = dataRows.filter(row => row[0]).map(row => ({ text: String(row[0]) }));
        if (newRows.length > 0) setTalentRows(newRows);
      } else {
        const jds = dataRows.filter(row => row[0]).map(row => ({ text: String(row[0]) }));
        const resumes = dataRows.filter(row => row[1]).map(row => ({ text: String(row[1]) }));
        if (jds.length > 0) setJdRows(jds);
        if (resumes.length > 0) setResumeRows(resumes);
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  // --- 분석 실행 ---
  const runAnalysis = async () => {
    if (activeTab === 'extraction') {
      if (rows.some(row => !row.text.trim() || row.value === "")) {
        showAlert("모든 행의 내용과 가중치를 입력해주세요."); return;
      }
      if (rows.some(row => parseFloat(row.value) <= 0)) {
        showAlert(`가중치에 0보다 작은 값이 있습니다.\n0 ~ 100 사이의 값으로 입력해주세요.😊`);
        return;
      }
    } else if (activeTab === 'talent') {
      if (talentRows.some(r => !r.text.trim())) {
        showAlert("모든 행의 내용을 입력해주세요."); return;
      }
    } else if (activeTab === 'search') {
      if (!searchName.trim()) {
        showAlert("이름을 입력해주세요."); return;
      }
    } else {
      if (jdRows.some(r => !r.text.trim()) || resumeRows.some(r => !r.text.trim())) {
        showAlert("JD와 Resume의 모든 칸을 입력해주세요."); return;
      }
    }

    setIsLoading(true);
    setShowAnalysis(false);
    setExpandedResultIndex(null);
    setProgress(0);
    setNewSkillsResult([]); 
    const apiUrl = process.env.REACT_APP_API_URL || '';

    let speedFactor = activeTab === 'extraction' ? Math.max(0.1, 1.0 - (rows.length * 0.05))
      : activeTab === 'talent' ? Math.max(0.1, 1.0 - (talentRows.length * 0.05))
      : activeTab === 'search' ? 0.8
      : Math.max(0.05, 1.0 - (jdRows.length * resumeRows.length * 0.05));

    progressRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev < 60) return prev + (0.65 * speedFactor);
        if (prev < 90) return prev + (0.35 * speedFactor);
        if (prev < 99) return prev + (0.1 * speedFactor);
        return 99;
      });
    }, 150);

    try {
      if (activeTab === 'extraction') {
        const processedRows = rows.map(row => ({ ...row, value: parseFloat(row.value) }));
        const response = await axios.post(`${apiUrl}/api/${activeClient}/analyze`, processedRows);

        if (response.data && response.data.status === "error") {
          throw new Error(response.data.message);
        }

        if (response.data && response.data.skills) {
          setTopSkills(response.data.skills);
          
          setErrorValue(response.data.residual_error); 
          setNeedsRecommend(response.data.needs_recommend);
          setTaskResiduals(response.data.task_residuals);
          
          // 결과가 추천 필요 상태면 알림 점 표시
          if (response.data.needs_recommend) {
            setShowBadge(true);
          }
        }
      } else if (activeTab === 'talent') {
        const talentData = { descriptions: talentRows.map(r => r.text) };
        const response = await axios.post(`${apiUrl}/api/${activeClient}/talent-match`, talentData);

        if (response.data && response.data.status === "error") {
          throw new Error(response.data.message);
        }

        setTalentProjectSkills(response.data.project_skills || []);
        setTalentCandidates(response.data.top_candidates || []);

      } else if (activeTab === 'search') {
        const response = await axios.post(`${apiUrl}/api/${activeClient}/employee-search`, { name: searchName.trim() });

        if (response.data && response.data.status === "error") {
          throw new Error(response.data.message);
        }

        setSearchEmployeeInfo({ name: response.data.name, department: response.data.department, job_title: response.data.job_title });
        setSearchEmployeeSkills(response.data.skills || []);
        setSearchSimilarEmployees(response.data.similar_employees || []);

      } else {
        const matchData = {
          jd_list: jdRows.map(r => r.text),
          resume_list: resumeRows.map(r => r.text)
        };
        const response = await axios.post(`${apiUrl}/api/${activeClient}/match`, matchData);

        if (response.data && response.data.status === "error") {
          throw new Error(response.data.message);
        }

        const flattenedResults = [];
        if (response.data.analysis_results) {
          response.data.analysis_results.forEach(jdResult => {
            jdResult.matches.forEach(match => {
              flattenedResults.push({
                jd_id: jdResult.jd_id,
                resume_id: match.resume_id ? match.resume_id.replace(/^res_/, 'can_') : match.resume_id,
                fit_score: (match.cosine_similarity * 100).toFixed(1) + "%",
                skills: match.skills
              });
            });
          });
        }
        setMatchResults(flattenedResults);
      }

      clearInterval(progressRef.current);
      setProgress(100);
      setTimeout(() => {
        setShowAnalysis(true);
        setIsLoading(false);
      }, 800);
    } catch (error) {
      clearInterval(progressRef.current);
      let finalMessage = "분석 중 오류 발생";
      if (!error.response) {
        finalMessage = "서버 연결이 안되어있습니다. 관리자에게 문의해주세요.";
      } else if (error.response.status === 504 || error.response.status === 502) {
        finalMessage = "서버 연결이 안되어있습니다. 관리자에게 문의해주세요.";
      } else {
        finalMessage = error.response?.data?.message || error.message || finalMessage;
      }

      showAlert(finalMessage);
      setIsLoading(false);
      setProgress(0);
    }
  };

  const runNewSkills = async () => {
    setIsNewSkillsLoading(true);
    setNewSkillsResult([]);
    setNewSkillsProgress(0);
    const apiUrl = process.env.REACT_APP_API_URL || '';

    newSkillsProgressRef.current = setInterval(() => {
        setNewSkillsProgress((prev) => {
          if (prev < 65) return prev + 0.8;
          if (prev < 92) return prev + 0.35;
          if (prev < 99) return prev + 0.1;
          return 99;
        });
      }, 150);
    
    try {
      const response = await axios.post(`${apiUrl}/api/${activeClient}/recommend`, taskResiduals);

      if (response.data && response.data.status === "error") {
        throw new Error(response.data.message);
      }

      clearInterval(newSkillsProgressRef.current);
      setNewSkillsProgress(100);
      
      setTimeout(() => {
        const recommendedNames = response.data.recommendations.map((item, idx) => 
          `Skill ${idx + 1} : ${item.skill_name}`
        );
        setNewSkillsResult(recommendedNames);
        setIsNewSkillsLoading(false);
      }, 800);

    } catch (error) {
      clearInterval(newSkillsProgressRef.current);
      showAlert("추천 스킬 도출 중 오류가 발생했습니다.");
      setIsNewSkillsLoading(false);
      setNewSkillsProgress(0);
    }
  };

  const toggleDetail = (index) => {
    const targetResult = matchResults[index];
    const scoreValue = parseFloat(targetResult.fit_score.replace('%', ''));

    if (scoreValue === 0 || !targetResult.skills || targetResult.skills.length === 0) {
      showAlert("분석결과가 없습니다. 입력값을 확인해주세요.");
      return;
    }

    setExpandedResultIndex(expandedResultIndex === index ? null : index);
  };

  if (!isAuthenticated()) {
    return <div className="App-loading">Redirecting to login...</div>;
  }

  return (
    <div className="App">
      {tooltip.visible && (
        <div className="skill-tooltip" style={{ left: tooltip.x, top: tooltip.y - 10, transform: 'translateY(-100%)' }}>
          {tooltip.text}
        </div>
      )}
      <div className="auth-header">
        {userInfo && <span>👤 {userInfo.email}</span>}
        <button onClick={logout} className="btn-logout">Logout</button>
      </div>

      <img src="/elizax_logo.png" alt="Elizax Logo" className="profile-img" />

      {alertConfig.isOpen && (
        <div className="modal-overlay">
          <div className="custom-alert-box">
            <div className={`alert-icon ${alertConfig.type}`}>
              {alertConfig.type === 'error' ? '!' : '✓'}
            </div>
            <p className="alert-message">{alertConfig.message}</p>
            <button className="alert-confirm-btn" onClick={() => setAlertConfig({ ...alertConfig, isOpen: false })}>
              확인
            </button>
          </div>
        </div>
      )}

      <aside className={`side-editor ${editorConfig.isOpen ? 'open' : ''}`}>
        <div className="editor-header">
          <h3>Full Content Editor</h3>
          <button className="btn-close-editor" onClick={() => setEditorConfig(prev => ({ ...prev, isOpen: false }))}>✕</button>
        </div>
        <div className="editor-subheader">
          <span className="editor-tag">
            {editorConfig.type === 'prompt' ? 'PROMPT'
              : editorConfig.type === 'skill_desc' ? `Description #${editorConfig.index + 1}`
              : `${editorConfig.type?.toUpperCase()} Item #${editorConfig.index + 1}`}
          </span>
        </div>
        <textarea
          className="editor-textarea"
          value={editorConfig.text}
          onChange={handleEditorChange}
          placeholder={editorConfig.type === 'prompt' ? SKILL_INIT_PROMPT_PLACEHOLDER : "상세 내용을 입력하세요..."}
        />
        <div className="editor-footer">
          <p>내용은 입력 리스트에 실시간으로 반영됩니다.</p>
        </div>
      </aside>

      <div className="guide-btn-container">
        <button
          className={`guide-toggle-btn ${isGuideOpen ? 'open' : ''}`}
          onClick={toggleGuide}
          title="User Guide"
        >
          {isGuideOpen ? '✕' : '💡'}
          
          {showBadge && !isGuideOpen && (
            <span className="notification-badge"></span>
          )}
        </button>
      </div>

      <aside className={`guide-memo ${isGuideOpen ? 'open' : ''}`}>
        <div className="memo-header">User Guide</div>
        <ul className="memo-content">
          {activeTab === 'team_composer' ? (
            <>
              <li>
                <strong>Project 내용</strong>
                <p>구성할 프로젝트의 내용을 자유롭게 입력하세요. 돋보기 버튼으로 큰 편집기에서 작성할 수 있습니다.</p>
              </li>
              <li>
                <strong>역할 구성</strong>
                <p>프로젝트 기반으로 도출된 역할별 카드가 표시됩니다. 각 카드는 주요 업무, 필요 역량, 추천 직원을 포함합니다.</p>
              </li>
              <li>
                <strong>추천 직원</strong>
                <p>각 역할에 가장 적합한 직원의 유사도와 Top-5 스킬을 확인할 수 있습니다.</p>
              </li>
              <li>
                <strong>신규 스킬 확장</strong>
                <p>프로젝트에 필요하지만 기존 스킬 베이스에 없는 스킬이 발견되면 확장 결과로 표시됩니다.</p>
              </li>
            </>
          ) : activeTab === 'skill_init' ? (
            <>
              <li>
                <strong>Excel 업로드</strong>
                <p>Skill명(한글)/Skill명(영어)/설명 컬럼이 있는 Excel을 업로드하세요. 중복 Skill이 포함되면 실행되지 않습니다.</p>
              </li>
              <li>
                <strong>Prompt</strong>
                <p>LLM에 전달할 시스템 프롬프트를 입력하세요. 비워두면 기본 프롬프트가 사용됩니다. 돋보기 버튼으로 큰 편집기를 열 수 있습니다.</p>
              </li>
              <li>
                <strong>Cosine / Clustering</strong>
                <p>Cosine은 유사 쌍 임계값, Clustering은 그룹 임계값입니다. 값을 조정하고 재실행하면 유사 Skill 결과가 갱신됩니다.</p>
              </li>
              <li>
                <strong>Run / 재실행</strong>
                <p>Run은 업로드 파일로 새 분석을 시작하고, 재실행은 편집된 결과를 사용해 임계값만 바꿔 다시 분석합니다.</p>
              </li>
              <li>
                <strong>유사 Skill</strong>
                <p>Cosine 기준 유사 쌍과 Clustering 기준 그룹이 표시됩니다. 항목을 클릭하면 Result 테이블의 해당 행으로 이동합니다.</p>
              </li>
              <li>
                <strong>Result 편집</strong>
                <p>셀을 직접 클릭해 수정할 수 있습니다. 편집 후 재실행을 권장합니다 (임베딩 정합성 유지).</p>
              </li>
              <li>
                <strong>DB 반영</strong>
                <p>최종 결과를 DB에 저장하고 Excel 파일도 함께 다운로드합니다.</p>
              </li>
            </>
          ) : activeTab === 'search' ? (
            <>
              <li>
                <strong>이름 입력</strong>
                <p>검색할 직원의 이름을 입력하세요.</p>
              </li>
              <li>
                <strong>Result</strong>
                <p>해당 직원의 스킬 분석과 유사한 스킬을 가진 직원 목록을 확인할 수 있습니다.</p>
              </li>
            </>
          ) : activeTab === 'talent' ? (
            <>
              <li>
                <strong>Task Description</strong>
                <p>추천받고자 하는 업무 내용을 상세히 기술하세요.</p>
              </li>
              <li>
                <strong>Result</strong>
                <p>입력한 업무와 가장 적합한 직원을 유사도 기반으로 추천합니다.</p>
              </li>
            </>
          ) : activeTab === 'extraction' ? (
            <>
              <li>
                <strong>Input 01. Type</strong>
                <p>T(Task) 또는 R(Resume)를 선택하여 데이터 성격을 구분하세요.</p>
              </li>
              <li>
                <strong>Input 02. Description</strong>
                <p>추출하고자 하는 업무 내용이나 역량을 상세히 기술하세요.</p>
              </li>
              <li>
                <strong>Input 03. Weight</strong>
                <p>각 항목의 중요도를 0에서 100 사이의 수치로 입력하세요.</p>
              </li>
              {/* [수정] 현재 화면에 New Skills 버튼이 노출될 때만 가이드 항목 표시 */}
              {needsRecommend && showAnalysis && (
                <li>
                  <strong>Update</strong>
                  <p>새로운 스킬이 생성되었습니다. 어떤 스킬이 생성되었는지 확인해주세요.</p>
                </li>
              )}
            </>
          ) : (
            <>
              <li>
                <strong>JD Input</strong>
                <p>채용 공고(JD) 내용을 입력하세요.</p>
              </li>
              <li>
                <strong>Resume Input</strong>
                <p>후보자의 이력서(Resume) 내용을 입력하세요.</p>
              </li>
              <li>
                <strong>Input Guide</strong>
                <p>분석결과가 안나온다면 괄호나 따옴표 등의 기호들을 삭제해주세요.</p>
              </li>
              <li>
                <strong>Match Analysis</strong>
                <p>Fit Score 옆의 버튼을 클릭해 상세 비교 차트를 확인하세요.</p>
              </li>
            </>
          )}
        </ul>
      </aside>

      <div className="client-selector-row">
        <select
          className="client-selector"
          value={activeClient}
          onChange={(e) => {
            setActiveClient(e.target.value);
            setShowAnalysis(false);
          }}
        >
          {clientList.length === 0
            ? <option value={activeClient}>{activeClient}</option>
            : clientList.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
        </select>
      </div>

      <div className={`tab-menu ${allowedTabs.length <= 1 ? 'single-tab' : ''}`}>
        {tabOrder.filter(id => allowedTabs.includes(id) && mainTabIds.includes(id)).map(id => {
          const tab = ALL_TABS.find(t => t.id === id);
          if (!tab) return null;
          return (
            <button
              key={tab.id}
              className={`tab-item ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => handleTabChange(tab.id)}
            >{tab.label}</button>
          );
        })}
        {isAdmin && (
          <div className="tab-more-wrapper"
            onMouseEnter={() => setIsMoreOpen(true)}
            onMouseLeave={() => setIsMoreOpen(false)}
          >
            <button className={`tab-item tab-more ${ALL_TABS.some(t => !mainTabIds.includes(t.id) && t.id === activeTab) ? 'active' : ''}`}>
              More <span className="tab-more-arrow">▾</span>
            </button>
            {isMoreOpen && (
              <div className="tab-more-menu">
                {tabOrder.filter(id => allowedTabs.includes(id) && !mainTabIds.includes(id)).map(id => {
                  const tab = ALL_TABS.find(t => t.id === id);
                  if (!tab) return null;
                  return (
                    <button
                      key={tab.id}
                      className={`tab-more-item ${activeTab === tab.id ? 'active' : ''}`}
                      onClick={() => { handleTabChange(tab.id); setIsMoreOpen(false); }}
                    >{tab.label}</button>
                  );
                })}
                <a href="https://demo-ui.hcg-ai.com/" className="tab-more-item tab-more-external">Agent Hub ↗</a>
                <button
                  className="tab-more-item tab-settings-item"
                  onClick={() => { setIsTabSettingsOpen(true); setIsMoreOpen(false); }}
                >⚙ 탭 설정</button>
              </div>
            )}
          </div>
        )}
      </div>

      {isTabSettingsOpen && (
        <div className="modal-overlay" onClick={() => setIsTabSettingsOpen(false)}>
          <div className="tab-settings-modal" onClick={e => e.stopPropagation()}>
            <div className="tab-settings-header">
              <h3>탭 설정</h3>
              <button className="btn-close-modal" onClick={() => setIsTabSettingsOpen(false)}>✕</button>
            </div>
            <p className="tab-settings-desc">드래그로 순서를 바꾸고, 체크박스로 메인 탭에 노출할지 선택하세요. 체크 해제된 항목은 More에 들어갑니다.</p>
            <ul className="tab-settings-list">
              {tabOrder.filter(id => allowedTabs.includes(id)).map(id => {
                const tab = ALL_TABS.find(t => t.id === id);
                if (!tab) return null;
                return (
                  <li
                    key={tab.id}
                    draggable
                    className={draggedTabId === tab.id ? 'dragging' : ''}
                    onDragStart={() => setDraggedTabId(tab.id)}
                    onDragOver={(e) => {
                      e.preventDefault();
                      if (!draggedTabId || draggedTabId === tab.id) return;
                      const newOrder = [...tabOrder];
                      const fromIdx = newOrder.indexOf(draggedTabId);
                      const toIdx = newOrder.indexOf(tab.id);
                      newOrder.splice(fromIdx, 1);
                      newOrder.splice(toIdx, 0, draggedTabId);
                      setTabOrder(newOrder);
                    }}
                    onDragEnd={() => setDraggedTabId(null)}
                  >
                    <span className="drag-handle">⋮⋮</span>
                    <label>
                      <input
                        type="checkbox"
                        checked={mainTabIds.includes(tab.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setMainTabIds([...mainTabIds, tab.id]);
                          } else {
                            setMainTabIds(mainTabIds.filter(id => id !== tab.id));
                          }
                        }}
                      />
                      <span>{tab.label}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      <div className="container">
        {activeTab === 'extraction' ? (
          <section>
            <div className="section-header"><h2>Description</h2></div>
            <div id="input-container">
              <div className="input-labels-row">
                <span className="col-type">TYPE</span>
                <span className="col-text">내용 (JOB DESCRIPTION / RESUME)</span>
                <span className="col-value">가중치 (0~100)</span>
                <span className="col-action"></span>
              </div>
              <div className="input-rows-wrapper">
                {rows.map((row, index) => (
                  <div className="input-row" key={index}>
                    <div className="col-type">
                      <select className="type-select" value={row.type} onChange={(e) => handleInputChange(index, 'type', e.target.value)}>
                        <option value="T">T</option><option value="R">R</option>
                      </select>
                    </div>
                    <div className="col-text">
                      <input type="text" className="text-input" placeholder="분석할 내용을 입력하세요" value={row.text} onChange={(e) => handleInputChange(index, 'text', e.target.value)} />
                    </div>
                    <div className="col-value">
                      <input type="number" step="1" className="num-input no-spinner" placeholder="0 ~ 100" value={row.value} onChange={(e) => handleInputChange(index, 'value', e.target.value)} />
                    </div>
                    <div className="col-action">
                      <button className="btn-delete" onClick={() => deleteRow(index)}>✕</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="button-actions-row">
              <button className="btn-add-line" onClick={addRow}>Add row +</button>
              <div className="btn-group-right">
                <div className="excel-btn-wrapper"
                  onMouseEnter={(e) => e.currentTarget.classList.add('show-download')}
                  onMouseLeave={(e) => e.currentTarget.classList.remove('show-download')}
                >
                  <label className="btn-run-main btn-excel">
                    Excel Upload
                    <input type="file" accept=".xlsx,.xls" onChange={handleExcelUpload} hidden />
                  </label>
                  <button className="btn-excel-download" onClick={downloadTemplate}>양식 다운로드</button>
                </div>
                <button className={`btn-run-main ${isLoading ? 'loading' : ''}`} onClick={runAnalysis} disabled={isLoading}>
                  {isLoading ? "Analyzing..." : "▶ Run Analysis"}
                </button>
              </div>
            </div>
          </section>
        ) : activeTab === 'talent' ? (
          <section>
            <div className="section-header"><h2>Task Description</h2></div>
            <div id="input-container">
              <div className="input-labels-row">
                <span className="col-num-label">순번</span>
                <span className="col-text">내용(업무 설명)</span>
                <span className="col-action"></span>
              </div>
              <div className="input-rows-wrapper">
                {talentRows.map((row, index) => (
                  <div className="input-row" key={index}>
                    <div className="col-num-label">
                      <span className="row-num">{index + 1}</span>
                    </div>
                    <div className="col-text">
                      <input type="text" className="text-input" placeholder="업무 내용을 입력하세요" value={row.text} onChange={(e) => handleTalentInputChange(index, e.target.value)} />
                    </div>
                    <div className="col-action">
                      <button className="btn-delete" onClick={() => deleteTalentRow(index)}>✕</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="button-actions-row">
              <button className="btn-add-line" onClick={addTalentRow}>Add row +</button>
              <div className="btn-group-right">
                <div className="excel-btn-wrapper"
                  onMouseEnter={(e) => e.currentTarget.classList.add('show-download')}
                  onMouseLeave={(e) => e.currentTarget.classList.remove('show-download')}
                >
                  <label className="btn-run-main btn-excel">
                    Excel Upload
                    <input type="file" accept=".xlsx,.xls" onChange={handleExcelUpload} hidden />
                  </label>
                  <button className="btn-excel-download" onClick={downloadTemplate}>양식 다운로드</button>
                </div>
                <button className={`btn-run-main ${isLoading ? 'loading' : ''}`} onClick={runAnalysis} disabled={isLoading}>
                  {isLoading ? "Analyzing..." : "▶ Run Analysis"}
                </button>
              </div>
            </div>
          </section>
        ) : activeTab === 'search' ? (
          <section>
            <div className="search-input-row">
              <span className="search-label">이름 :</span>
              <input
                type="text"
                className="search-input"
                placeholder="직원 이름을 입력하세요"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !isLoading) runAnalysis(); }}
              />
              <button className={`btn-run-main ${isLoading ? 'loading' : ''}`} onClick={runAnalysis} disabled={isLoading}>
                {isLoading ? "검색 중..." : "검색"}
              </button>
            </div>
          </section>
        ) : activeTab === 'skill_init' ? (
          <section>
            <div className="section-header"><h2>Skill Init</h2></div>
            <div className={`skill-init-notice ${noticeOpen ? 'open' : ''}`}>
              <button type="button" className="notice-header" onClick={() => setNoticeOpen(!noticeOpen)}>
                <span className="notice-toggle">{noticeOpen ? '▾' : '▸'}</span>
                <span className="notice-icon">⚠️</span>
                <span className="notice-title">
                  {noticeOpen ? '유의사항' : '시작전에 유의사항을 확인해주세요'}
                </span>
              </button>
              {noticeOpen && (
                <div className="notice-body">
                  <ol className="notice-list">
                    <li>
                      <strong>필수 입력 컬럼</strong>
                      <p>최초 입력 시 <code>스킬명(한)</code>, <code>스킬명(영)</code>, <code>설명</code> 세 컬럼은 반드시 입력해야 합니다. (<code>.xlsx</code> 양식 참고)</p>
                      <p>예외로, 재분석을 위한 최종 결과 파일(<code>skill_init_result.xlsx</code>) 재투입 시에만 정상 동작합니다.</p>
                    </li>
                    <li>
                      <strong>중복 스킬 불가</strong>
                      <p>스킬명(영) 기준으로 중복이 있을 경우 실행이 중단됩니다. 입력 전 중복 여부를 반드시 확인해주세요.</p>
                    </li>
                    <li>
                      <strong>컬럼명 변경 불가</strong>
                      <p>양식 파일의 컬럼명은 변경할 수 없습니다. 추가 컬럼이 있더라도 양식에 정의된 컬럼만 인식합니다.</p>
                    </li>
                    <li>
                      <strong>유사도 분석 기준</strong>
                      <p>유사도 점수는 스킬명이 아닌 <code>description</code> 내용을 기준으로 산출됩니다. Description의 품질이 분석 결과에 직접적인 영향을 줍니다.</p>
                    </li>
                    <li>
                      <strong>프롬프트 수정 주의</strong>
                      <p>프롬프트 내 <code>Description Guidelines</code>, <code>Output Format</code> 섹션은 분석 결과의 일관성을 위해 내용 변경을 지양해주세요.</p>
                    </li>
                  </ol>
                </div>
              )}
            </div>
            <div className="skill-init-row">
              <div className="excel-btn-wrapper skill-init-upload-wrapper"
                onMouseEnter={(e) => e.currentTarget.classList.add('show-download')}
                onMouseLeave={(e) => e.currentTarget.classList.remove('show-download')}
              >
                <label className="skill-init-upload">
                  <span className="skill-init-upload-label">
                    {skillInitFile ? `📄 ${skillInitFile.name}` : '📁 Excel 파일 업로드'}
                  </span>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    hidden
                    onChange={(e) => setSkillInitFile(e.target.files[0] || null)}
                  />
                </label>
                <button className="btn-excel-download" onClick={downloadTemplate}>양식 다운로드</button>
              </div>
              <input
                type="text"
                className="skill-init-prompt"
                placeholder={SKILL_INIT_PROMPT_PLACEHOLDER}
                value={skillInitPrompt}
                onChange={(e) => setSkillInitPrompt(e.target.value)}
              />
              <button className="btn-open-editor" title="상세 편집" onClick={() => openEditor('prompt', 0)}>🔍</button>
            </div>
            <div className="skill-init-params-row">
              <label className="threshold-label">
                Cosine
                <input type="number" step="0.01" min="0" max="1"
                  value={skillInitCosine}
                  onChange={(e) => setSkillInitCosine(e.target.value)} />
              </label>
              <label className="threshold-label">
                Clustering
                <input type="number" step="0.01" min="0" max="1"
                  value={skillInitClustering}
                  onChange={(e) => setSkillInitClustering(e.target.value)} />
              </label>
            </div>
            <div className="button-actions-row" style={{ justifyContent: 'flex-end', gap: '10px' }}>
              {skillInitResults.length > 0 && (
                <button
                  className={`btn-secondary ${isLoading ? 'loading' : ''}`}
                  onClick={rerunSkillInit}
                  disabled={isLoading}
                  title="편집된 데이터와 현재 임계값으로 다시 분석"
                >
                  {isLoading ? "재실행 중..." : "🔄 재실행"}
                </button>
              )}
              <button
                className={`btn-run-main ${isLoading ? 'loading' : ''}`}
                onClick={runSkillInit}
                disabled={isLoading}
                title="업로드한 파일과 Prompt로 새 분석 시작"
              >
                {isLoading ? "Processing..." : "▶ Run"}
              </button>
            </div>

            {skillInitResults.length > 0 && (
              <div className="skill-init-results">
                {/* 유사 Skill 영역 — 결과 테이블 위 */}
                <div className="similar-skill-section">
                  <div className="section-header">
                    <h2>유사 Skill</h2>
                  </div>

                  <div className={`similar-block ${cosineOpen ? '' : 'collapsed'}`}>
                    <button type="button" className="similar-block-header" onClick={() => setCosineOpen(!cosineOpen)}>
                      <span className="similar-block-toggle">{cosineOpen ? '▾' : '▸'}</span>
                      <span className="similar-block-title">Cosine 유사 쌍</span>
                      <span className="similar-block-count">{skillInitSimPairs.length}쌍</span>
                      <span className="similar-block-value">현재 임계값: {skillInitCosine}</span>
                    </button>
                    {cosineOpen && (skillInitSimPairs.length > 0 ? (
                      <div className="similar-groups">
                        {skillInitSimPairs.map((pair, i) => (
                          <div className="similar-group" key={i}>
                            <div className="similar-group-header">
                              Pair {i + 1}
                              <span className="similar-group-reason">— cosine {Number(pair.cosine_sim).toFixed(4)}</span>
                            </div>
                            <div className="similar-group-items">
                              {[pair.idx_a, pair.idx_b].map(memberIdx => {
                                const skill = skillInitSnapshot[memberIdx];
                                if (!skill) return null;
                                const liveRow = skillInitResults.find(r => r._uid === skill._uid);
                                const isRemoved = !liveRow || liveRow._deleted;
                                return (
                                  <div
                                    className={`similar-group-item ${isRemoved ? 'removed' : 'clickable'}`}
                                    key={memberIdx}
                                    onClick={() => !isRemoved && scrollToSkillRowByUid(skill._uid)}
                                    title={isRemoved ? "Result에서 제거된 항목" : "결과 테이블의 해당 행으로 이동"}
                                  >
                                    <span className="similar-skill-en">{skill.skill_name}</span>
                                    <span className="similar-skill-ko">{skill.skill_name_ko}</span>
                                    {isRemoved && (
                                      <button
                                        className="similar-skill-restore"
                                        onClick={(e) => { e.stopPropagation(); restoreSkillInitRow(skill._uid); }}
                                        title="이 Skill을 Result에 다시 추가합니다"
                                      >↻ 복구</button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="similar-empty">Cosine 기준 유사 쌍이 없습니다.</div>
                    ))}
                  </div>

                  <div className={`similar-block ${clusterOpen ? '' : 'collapsed'}`}>
                    <button type="button" className="similar-block-header" onClick={() => setClusterOpen(!clusterOpen)}>
                      <span className="similar-block-toggle">{clusterOpen ? '▾' : '▸'}</span>
                      <span className="similar-block-title">Clustering 그룹</span>
                      <span className="similar-block-count">{skillInitGroups.length}개</span>
                      <span className="similar-block-value">현재 임계값: {skillInitClustering}</span>
                    </button>
                    {clusterOpen && (skillInitGroups.length > 0 ? (
                      <div className="similar-groups">
                        {skillInitGroups.map((group) => (
                          <div className="similar-group" key={group.group_id}>
                            <div className="similar-group-header">
                              그룹 {group.group_id} ({group.size}개)
                              {typeof group.min_cosine === 'number' && (
                                <span className="similar-group-reason">— min cosine {Number(group.min_cosine).toFixed(4)}</span>
                              )}
                            </div>
                            <div className="similar-group-items">
                              {(group.indices || []).map(memberIdx => {
                                const skill = skillInitSnapshot[memberIdx];
                                if (!skill) return null;
                                const liveRow = skillInitResults.find(r => r._uid === skill._uid);
                                const isRemoved = !liveRow || liveRow._deleted;
                                return (
                                  <div
                                    className={`similar-group-item ${isRemoved ? 'removed' : 'clickable'}`}
                                    key={memberIdx}
                                    onClick={() => !isRemoved && scrollToSkillRowByUid(skill._uid)}
                                    title={isRemoved ? "Result에서 제거된 항목" : "결과 테이블의 해당 행으로 이동"}
                                  >
                                    <span className="similar-skill-en">{skill.skill_name}</span>
                                    <span className="similar-skill-ko">{skill.skill_name_ko}</span>
                                    {isRemoved && (
                                      <button
                                        className="similar-skill-restore"
                                        onClick={(e) => { e.stopPropagation(); restoreSkillInitRow(skill._uid); }}
                                        title="이 Skill을 Result에 다시 추가합니다"
                                      >↻ 복구</button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="similar-empty">Clustering 기준 그룹이 없습니다.</div>
                    ))}
                  </div>
                </div>

                <div className="section-header skill-init-results-header">
                  <h2>Result</h2>
                  <div className="skill-init-actions">
                    <button className={`btn-run-main ${isSkillInitSaving ? 'loading' : ''}`} onClick={saveSkillInitToDB} disabled={isSkillInitSaving}>
                      {isSkillInitSaving ? "처리 중..." : "💾 DB 반영"}
                    </button>
                  </div>
                </div>
                <div className="skill-init-toolbar">
                  <div className="skill-init-search-wrapper">
                    <span className="search-icon">🔍</span>
                    <input
                      type="text"
                      className="skill-init-search"
                      placeholder="Skill명(영어/한글)으로 검색..."
                      value={skillInitSearch}
                      onChange={(e) => setSkillInitSearch(e.target.value)}
                    />
                    {skillInitSearch && (
                      <button className="skill-init-search-clear" onClick={() => setSkillInitSearch('')}>✕</button>
                    )}
                  </div>
                  <span className="skill-init-count">
                    {(() => {
                      const q = skillInitSearch.toLowerCase().trim();
                      const visible = q
                        ? skillInitResults.filter(r => (r.skill_name || '').toLowerCase().includes(q) || (r.skill_name_ko || '').toLowerCase().includes(q)).length
                        : skillInitResults.length;
                      const pending = skillInitResults.filter(r => r._deleted).length;
                      return (
                        <>
                          {`${visible} / ${skillInitResults.length}`}
                          {pending > 0 && <span className="pending-delete-count"> · 삭제 예정 {pending}</span>}
                        </>
                      );
                    })()}
                  </span>
                </div>
                <div className="skill-init-table-wrapper">
                  <table className="result-table skill-init-table">
                    <thead>
                      <tr>
                        <th style={{ width: '5%' }}>#</th>
                        <th style={{ width: '22%' }}>skill_name</th>
                        <th style={{ width: '22%' }}>skill_name_ko</th>
                        <th style={{ width: '46%' }}>description</th>
                        <th style={{ width: '5%' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {skillInitResults
                        .map((row, idx) => ({ row, idx }))
                        .filter(({ row }) => {
                          const q = skillInitSearch.toLowerCase().trim();
                          if (!q) return true;
                          return (row.skill_name || '').toLowerCase().includes(q)
                              || (row.skill_name_ko || '').toLowerCase().includes(q);
                        })
                        .map(({ row, idx }) => {
                      return (
                      <tr
                        key={idx}
                        data-row-idx={idx}
                        className={`${skillInitHighlightIdx === idx ? 'row-highlight' : ''} ${row._deleted ? 'row-pending-delete' : ''}`}
                      >
                        <td className="row-index-cell">{idx + 1}</td>
                        <td className="readonly-cell">
                          <span className="cell-text">{row.skill_name || ''}</span>
                        </td>
                        <td className="readonly-cell">
                          <span className="cell-text">{row.skill_name_ko || ''}</span>
                        </td>
                        <td className="desc-cell">
                          {row._deleted ? (
                            <span className="desc-text desc-disabled">{row.description}</span>
                          ) : (
                            <button
                              type="button"
                              className={`desc-preview ${editorConfig.isOpen && editorConfig.type === 'skill_desc' && editorConfig.index === idx ? 'editing' : ''}`}
                              onClick={() => openEditor('skill_desc', idx)}
                              title="클릭하여 편집"
                            >
                              {row.description
                                ? <span className="desc-text">{row.description}</span>
                                : <span className="desc-empty">설명을 입력하려면 클릭하세요</span>}
                              <span className="desc-edit-icon">✎</span>
                            </button>
                          )}
                        </td>
                        <td>
                          {row._deleted ? (
                            <button className="btn-restore-row" onClick={() => deleteSkillInitRow(idx)} title="삭제 취소">↩</button>
                          ) : (
                            <button className="btn-delete" onClick={() => deleteSkillInitRow(idx)} title="삭제 예정으로 표시">✕</button>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>
        ) : activeTab === 'team_composer' ? (
          <section>
            <div className="section-header"><h2>Team Composer</h2></div>
            <div className="team-input-row">
              <span className="team-input-label">Project 내용 :</span>
              <input
                type="text"
                className="team-input"
                placeholder="프로젝트 내용을 입력하세요"
                value={teamProject}
                onChange={(e) => setTeamProject(e.target.value)}
              />
              <button className="btn-open-editor team-editor-btn" title="상세 편집" onClick={() => openEditor('team_project', 0)}>🔍</button>
            </div>
            <div className="button-actions-row" style={{ justifyContent: 'flex-end' }}>
              <button className={`btn-run-main ${isLoading ? 'loading' : ''}`} onClick={runTeamComposer} disabled={isLoading}>
                {isLoading ? "Processing..." : "▶ Run"}
              </button>
            </div>
          </section>
        ) : (
          <section>
            <div className="match-grid">
              <div className="match-col">
                <div className="section-header"><h2>JD (Job Description)</h2></div>
                <div className="match-input-box"> 
                  {jdRows.map((row, index) => (
                    <div className="input-row" key={index}>
                      <span className="row-num">{index + 1}.</span>
                      <input type="text" placeholder="요구 역량 입력" value={row.text} onChange={(e) => handleMatchInputChange('jd', index, e.target.value)} />
                      <button className="btn-open-editor" title="상세 편집" onClick={() => openEditor('jd', index)}>🔍</button>
                      <button className="btn-delete" onClick={() => deleteMatchRow('jd', index)}>✕</button>
                    </div>
                  ))}
                </div>
                <button className="btn-add-line" onClick={() => addMatchRow('jd')}>Add JD row +</button>
              </div>
              <div className="match-col">
                <div className="section-header"><h2>Candidate</h2></div>
                <div className="match-input-box">
                  {resumeRows.map((row, index) => (
                    <div className="input-row" key={index}>
                      <span className="row-num">{index + 1}.</span>
                      <input type="text" placeholder="보유 역량 입력" value={row.text} onChange={(e) => handleMatchInputChange('resume', index, e.target.value)} />
                      <button className="btn-open-editor" title="상세 편집" onClick={() => openEditor('resume', index)}>🔍</button>
                      <button className="btn-delete" onClick={() => deleteMatchRow('resume', index)}>✕</button>
                    </div>
                  ))}
                </div>
                <button className="btn-add-line" onClick={() => addMatchRow('resume')}>Add Candidate row +</button>
              </div>
            </div>
            <div className="button-actions-row" style={{ justifyContent: 'flex-end' }}>
              <div className="btn-group-right">
                <div className="excel-btn-wrapper"
                  onMouseEnter={(e) => e.currentTarget.classList.add('show-download')}
                  onMouseLeave={(e) => e.currentTarget.classList.remove('show-download')}
                >
                  <label className="btn-run-main btn-excel">
                    Excel Upload
                    <input type="file" accept=".xlsx,.xls" onChange={handleExcelUpload} hidden />
                  </label>
                  <button className="btn-excel-download" onClick={downloadTemplate}>양식 다운로드</button>
                </div>
                <button className={`btn-run-main ${isLoading ? 'loading' : ''}`} onClick={runAnalysis} disabled={isLoading}>
                  {isLoading ? "Analyzing..." : "▶ Run Analysis"}
                </button>
              </div>
            </div>
          </section>
        )}

        {isLoading && (
          <div className="loading-status-container">
            <p className="loading-text">
              {progress < 30 && `데이터를 전송 중입니다... (${Math.floor(progress)}%)`}
              {progress >= 30 && progress < 80 && `AI 모델이 데이터를 분석하고 있습니다... (${Math.floor(progress)}%)`}
              {progress >= 80 && progress < 99 && `분석 결과를 생성하는 중입니다... (${Math.floor(progress)}%)`}
              {progress >= 99 && `마무리를 하고 있습니다. 잠시만 기다려 주세요! (${Math.floor(progress)}%)`}
            </p>
            <div className="loading-bar-wrapper">
              <div className="loading-bar-real-progress" style={{ width: `${progress}%` }}></div>
            </div>
          </div>
        )}

        {showAnalysis && (
          <section className="analysis-section">
            {activeTab !== 'talent' && activeTab !== 'search' && activeTab !== 'team_composer' && (
              <div className="section-header"><h2>Analysis</h2></div>
            )}
            {activeTab === 'team_composer' ? (
              <>
                {/* 역할별 카드 */}
                {teamRoles.length > 0 && (
                  <div className="team-roles-section">
                    <div className="section-header"><h2>역할 구성</h2></div>
                    {teamRoles.map((role, rIdx) => {
                      const isOpen = expandedRoleIdx === rIdx;
                      return (
                        <div className={`role-card ${isOpen ? 'open' : ''}`} key={rIdx}>
                          <button
                            type="button"
                            className="role-card-header"
                            onClick={() => setExpandedRoleIdx(isOpen ? null : rIdx)}
                          >
                            <span className="role-card-toggle">{isOpen ? '▾' : '▸'}</span>
                            <span className="role-card-title">{role.role}</span>
                            <span className="role-card-badge">{role.recommended_employees?.length || 0}명 추천</span>
                          </button>
                          {isOpen && (
                            <div className="role-card-body">
                              {/* Tasks */}
                              {role.tasks && role.tasks.length > 0 && (
                                <div className="role-tasks">
                                  <h4 className="role-section-title">주요 업무</h4>
                                  <div className="role-tasks-list">
                                    {role.tasks.map((t, ti) => (
                                      <span className="task-chip" key={ti}>{t}</span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Required Skills */}
                              {role.role_top_skills && role.role_top_skills.length > 0 && (
                                <div className="role-required-skills">
                                  <h4 className="role-section-title">필요 역량</h4>
                                  {role.role_top_skills.map((s, si) => {
                                    const pct = `${(s.weight * 100).toFixed(1)}%`;
                                    return (
                                      <div className="chart-row" key={si}>
                                        <div className="chart-label">{s.skill}</div>
                                        <div className="bar-flex-container">
                                          <div className="bar-container">
                                            <div className="bar-fill" style={{ width: pct }}></div>
                                          </div>
                                          <span className="percentage-label">{pct}</span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {/* Recommended Employees */}
                              {role.recommended_employees && role.recommended_employees.length > 0 && (
                                <div className="role-recommended">
                                  <h4 className="role-section-title">추천 직원</h4>
                                  <table className="result-table role-employee-table">
                                    <thead>
                                      <tr>
                                        <th>순위</th><th>직원</th><th>직책</th><th>유사도</th><th>Top-5 스킬</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {role.recommended_employees.map((emp, ei) => {
                                        const maxSim = role.recommended_employees[0]?.cos_w || 1;
                                        const opacity = 0.45 + 0.55 * ((emp.cos_w || 0) / maxSim);
                                        return (
                                          <tr key={ei} style={{ opacity }}>
                                            <td className="rank-cell">
                                              <span className={`rank-badge rank-${ei + 1}`}>{ei + 1}순위</span>
                                            </td>
                                            <td>{emp.name}</td>
                                            <td>{emp.org}</td>
                                            <td><span className="match-badge high">{((emp.cos_w || 0) * 100).toFixed(1)}%</span></td>
                                            <td className="top-skills-cell">
                                              {emp.top_skills && emp.top_skills.map((skill, idx) => (
                                                <div key={idx} className="skill-bar-row">
                                                  <span
                                                    className="skill-bar-name"
                                                    onMouseEnter={(e) => { const r = e.target.getBoundingClientRect(); setTooltip({ visible: true, text: skill.skill, x: r.left, y: r.top - 8 }); }}
                                                    onMouseLeave={() => setTooltip({ ...tooltip, visible: false })}
                                                  >{skill.skill}</span>
                                                  <div className="skill-bar-track">
                                                    <div className="skill-bar-fill" style={{ width: `${(skill.weight * 100).toFixed(1)}%` }}></div>
                                                  </div>
                                                  <span className="skill-bar-pct">{(skill.weight * 100).toFixed(1)}%</span>
                                                </div>
                                              ))}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Skill Base Expansion */}
                {teamExpansion && teamExpansion.expanded && (teamExpansion.post_sim_check?.length > 0) && (
                  <div className="sbase-expansion-section">
                    <div className="section-header"><h2>신규 스킬 확장</h2></div>
                    <p className="sbase-expansion-desc">프로젝트에 필요한 새로운 스킬이 식별되었습니다.</p>
                    <div className="sbase-grid">
                      {teamExpansion.post_sim_check.map((s, i) => (
                        <div className="sbase-card" key={i}>
                          <div className="sbase-card-header">
                            <span className="sbase-skill-name">{s.skill_name}</span>
                          </div>
                          <p className="sbase-desc">{s.skill_description}</p>
                          {s.nearest_top3 && s.nearest_top3.length > 0 && (
                            <div className="sbase-nearest">
                              <span className="sbase-nearest-label">유사 기존 스킬:</span>
                              <ul>
                                {s.nearest_top3.map((n, ni) => (
                                  <li key={ni}>{n.skill} <span className="sbase-sim">({Number(n.sim).toFixed(3)})</span></li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : activeTab === 'search' ? (
              <>
                {/* 직원 스킬 분석 */}
                {searchEmployeeSkills.length > 0 && (
                  <div className="talent-skill-section">
                    <div className="section-header">
                      <h2>{searchEmployeeInfo?.name} ({searchEmployeeInfo?.department} / {searchEmployeeInfo?.job_title})</h2>
                    </div>
                    {searchEmployeeSkills.map((item, idx) => (
                      <div className="chart-row" key={idx}>
                        <div className="chart-label">{item.skill}</div>
                        <div className="bar-flex-container">
                          <div className="bar-container">
                            <div className="bar-fill" style={{ width: item.percentage || '0%' }}></div>
                          </div>
                          <span className="percentage-label">{item.percentage}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* 유사 직원 */}
                {searchSimilarEmployees.length > 0 ? (
                  <div className="talent-candidate-section">
                    <div className="section-header"><h2>유사 직원 리스트</h2></div>
                    <table className="result-table talent-table">
                      <thead>
                        <tr>
                          <th>직원</th><th>소속</th><th>직무</th><th>유사도</th><th>Top-5 스킬</th>
                        </tr>
                      </thead>
                      <tbody>
                        {searchSimilarEmployees.map((res, i) => {
                          const maxSim = searchSimilarEmployees[0]?.similarity || 1;
                          const opacity = 0.45 + 0.55 * (res.similarity / maxSim);
                          return (
                            <tr key={i} style={{ opacity }}>
                              <td>{res.name}</td>
                              <td>{res.department}</td>
                              <td>{res.job_title}</td>
                              <td><span className="match-badge high">{(res.similarity * 100).toFixed(1)}%</span></td>
                              <td className="top-skills-cell">
                                {res.skills && res.skills.map((skill, idx) => (
                                  <div key={idx} className="skill-bar-row">
                                    <span className="skill-bar-name"
                                      onMouseEnter={(e) => { const r = e.target.getBoundingClientRect(); setTooltip({ visible: true, text: skill.skill, x: r.left, y: r.top - 8 }); }}
                                      onMouseLeave={() => setTooltip({ ...tooltip, visible: false })}
                                    >{skill.skill}</span>
                                    <div className="skill-bar-track">
                                      <div className="skill-bar-fill" style={{ width: skill.percentage || '0%' }}></div>
                                    </div>
                                    <span className="skill-bar-pct">{skill.percentage}</span>
                                  </div>
                                ))}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : <div className="no-result-container"><p>유사 직원이 없습니다.</p></div>}
              </>
            ) : activeTab === 'talent' ? (
              <>
                {/* Required Skill Analysis */}
                {talentProjectSkills.length > 0 && (
                  <div className="talent-skill-section">
                    <div className="section-header"><h2>Required Skill Analysis</h2></div>
                    {talentProjectSkills.map((item, idx) => (
                      <div className="chart-row" key={idx}>
                        <div className="chart-label">{item.skill}</div>
                        <div className="bar-flex-container">
                          <div className="bar-container">
                            <div className="bar-fill" style={{ width: item.percentage || '0%' }}></div>
                          </div>
                          <span className="percentage-label">{item.percentage}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* 적임자 추천 */}
                {talentCandidates.length > 0 ? (
                  <div className="talent-candidate-section">
                    <div className="section-header"><h2>적임자 추천</h2></div>
                    <table className="result-table talent-table">
                      <thead>
                        <tr>
                          <th>직원</th><th>소속</th><th>직무</th><th>유사도</th><th>Top-5 스킬</th>
                        </tr>
                      </thead>
                      <tbody>
                        {talentCandidates.map((res, i) => {
                          const maxSim = talentCandidates[0]?.similarity || 1;
                          const opacity = 0.45 + 0.55 * (res.similarity / maxSim);
                          return (
                            <tr key={i} style={{ opacity }}>
                              <td>{res.name}</td>
                              <td>{res.department}</td>
                              <td>{res.job_title}</td>
                              <td><span className="match-badge high">{(res.similarity * 100).toFixed(1)}%</span></td>
                              <td className="top-skills-cell">
                                {res.skills && res.skills.map((skill, idx) => (
                                  <div key={idx} className="skill-bar-row">
                                    <span className="skill-bar-name"
                                      onMouseEnter={(e) => { const r = e.target.getBoundingClientRect(); setTooltip({ visible: true, text: skill.skill, x: r.left, y: r.top - 8 }); }}
                                      onMouseLeave={() => setTooltip({ ...tooltip, visible: false })}
                                    >{skill.skill}</span>
                                    <div className="skill-bar-track">
                                      <div className="skill-bar-fill" style={{ width: skill.percentage || '0%' }}></div>
                                    </div>
                                    <span className="skill-bar-pct">{skill.percentage}</span>
                                  </div>
                                ))}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : <div className="no-result-container"><p>분석 결과가 없습니다.</p></div>}
              </>
            ) : activeTab === 'extraction' ? (
              <>
                {topSkills.length > 0 ? (
                  topSkills.map((item, idx) => (
                    <div className="chart-row" key={idx}>
                      <div className="chart-label">{item.skill}</div>
                      <div className="bar-flex-container">
                        <div className="bar-container">
                          <div className="bar-fill" style={{ width: item.percentage || '0%' }}></div>
                        </div>
                        <span className="percentage-label">{item.percentage}</span>
                      </div>
                    </div>
                  ))
                ) : <div className="no-result-container"><p>분석 결과가 없습니다.</p></div>}
                
                {topSkills.length > 0 && (
                  <div className="update-section">
                    <div className="section-header update-header-with-btn">
                      <h2>Update</h2>
                      {needsRecommend && (
                        <button 
                          className={`btn-run-main btn-new-skills-elizax ${isNewSkillsLoading ? 'loading' : ''}`} 
                          onClick={runNewSkills} 
                          disabled={isNewSkillsLoading}
                        >
                          {isNewSkillsLoading ? "Processing..." : "New Skills"}
                        </button>
                      )}
                    </div>

                    <div className="error-display-box">
                      <span className="error-label">Skill Alignment Gap :</span>
                      <span className="error-value">{errorValue !== null ? errorValue.toFixed(4) : "0.0000"}</span>
                    </div>

                    {isNewSkillsLoading && (
                      <div className="loading-status-container" style={{ marginTop: '20px' }}>
                        <p className="loading-text">신규 역량을 도출하는 중입니다... ({Math.floor(newSkillsProgress)}%)</p>
                        <div className="loading-bar-wrapper">
                          <div className="loading-bar-real-progress" style={{ width: `${newSkillsProgress}%`, backgroundColor: 'var(--elizax-blue)' }}></div>
                        </div>
                      </div>
                    )}

                    {!isNewSkillsLoading && newSkillsResult.length > 0 && (
                      <div className="new-skills-result-box elizax-theme">
                        <h4>New Skills Analysis</h4>
                        <div className="new-skills-list">
                          {newSkillsResult.map((skill, idx) => (
                            <p key={idx} className="skill-item">{skill}</p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="result-table-container">
                <table className="result-table">
                  <thead>
                    <tr>
                      <th>JD ID</th><th>Candidate ID</th><th>Fit Score</th><th>Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matchResults.map((res, i) => (
                      <React.Fragment key={i}>
                        <tr>
                          <td>{res.jd_id}</td>
                          <td>{res.resume_id}</td>
                          <td><span className="match-badge high">{res.fit_score}</span></td>
                          <td>
                            <button className="btn-detail" onClick={() => toggleDetail(i)}>
                              {expandedResultIndex === i ? 'Close' : 'View Chart'}
                            </button>
                          </td>
                        </tr>
                        {expandedResultIndex === i && (
                          <tr>
                            <td colSpan="4" className="chart-expand-cell">
                              <div className="detail-chart-wrapper">
                                <div className="chart-close-row">
                                  <button onClick={() => setExpandedResultIndex(null)}>✕ Close Chart</button>
                                </div>
                                <div className="chart-info">
                                  <h4>Skill Match Details</h4>
                                  <p>{res.jd_id}와 {res.resume_id}의 스킬별 가중치 비교</p>
                                </div>
                                <ResponsiveContainer width="100%" height={350}>
                                  <BarChart
                                    data={res.skills}
                                    margin={{ top: 20, right: 30, left: 10, bottom: 40 }}
                                    barGap={8}
                                  >
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                                    <XAxis
                                      dataKey="name"
                                      tick={{ fontSize: 12, fontWeight: 600, fill: '#4A5568' }}
                                      interval={0} angle={-20} textAnchor="end" height={70} stroke="#CBD5E0"
                                    />
                                    <YAxis
                                      tick={{ fontSize: 12, fill: '#718096' }}
                                      stroke="#CBD5E0"
                                      domain={[0, 'auto']}
                                      allowDataOverflow={false}
                                    />
                                    <Tooltip cursor={{ fill: '#F7FAFC' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '12px' }} />
                                    <Legend verticalAlign="top" align="right" iconType="circle" wrapperStyle={{ paddingBottom: '20px', fontSize: '13px', fontWeight: 600 }} />
                                    <Bar name="JD Required" dataKey="w_jd" fill="var(--elizax-blue)" radius={[4, 4, 0, 0]} barSize={24} animationDuration={1500} />
                                    <Bar name="Candidate" dataKey="w_resume" fill="var(--elizax-light-blue)" radius={[4, 4, 0, 0]} barSize={24} animationDuration={1500} />
                                  </BarChart>
                                </ResponsiveContainer>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

export default App;